/**
 * cleanupDuplicatePhaseOpsServer.js
 *
 * FT-219: Clean duplicate phase operations from Azure PostgreSQL server
 *
 * On each sr:sync:hydrate event for voyage_settings, immediately check that
 * vessel's data for duplicate phaseTypes. If duplicates found, clean localStorage
 * and push to server. No global flag needed - once server data is clean, future
 * hydrations find nothing to fix and skip the push.
 *
 * TIMING: Runs on each voyage_settings hydrate event (after sync pulls data)
 */

import { azureCloudClient } from '../../sync/azureCloudClient';
import { devLog, devError } from '../devLog';

/**
 * Register listener for voyage_settings hydration events
 * Call this once at app startup (in index.js after migrations)
 */
export const registerServerCleanupListener = () => {
  devLog('[FT-219-SERVER] Registering per-hydrate cleanup listener');

  // Listen for hydrate events on voyage_settings
  // Note: baseKey in hydrate event is 'voyage_settings' (from MODULE_MAP key), not 'sr.voyage_settings.v1'
  window.addEventListener('sr:sync:hydrate', async (event) => {
    const { module, vesselId } = event.detail || {};

    if (module !== 'voyage_settings' || !vesselId) return;

    // Find the localStorage key for this vessel's voyage_settings
    // Key format: voyage_settings_{orgId}_{vesselId}
    let localKey = null;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('voyage_settings_') && key.endsWith(`_${vesselId}`)) {
        localKey = key;
        break;
      }
    }

    if (!localKey) {
      return; // No voyage_settings for this vessel
    }

    const settingsJson = localStorage.getItem(localKey);
    if (!settingsJson) return;

    let settings;
    try {
      settings = JSON.parse(settingsJson);
    } catch (e) {
      devError(`[FT-219-SERVER] Failed to parse ${localKey}:`, e);
      return;
    }

    // Check if operationsList exists
    if (!settings?.operationsList || !Array.isArray(settings.operationsList)) {
      return;
    }

    // Deduplicate by phaseType
    const seenPhaseTypes = new Set();
    const deduplicatedList = [];
    let removedCount = 0;

    for (const op of settings.operationsList) {
      if (op.phaseType) {
        if (seenPhaseTypes.has(op.phaseType)) {
          removedCount++;
          devLog(`[FT-219-SERVER] Removing duplicate ${op.phaseType} from ${localKey}`);
        } else {
          seenPhaseTypes.add(op.phaseType);
          deduplicatedList.push(op);
        }
      } else {
        deduplicatedList.push(op);
      }
    }

    if (removedCount === 0) {
      return; // No duplicates, nothing to do
    }

    // Update localStorage
    settings.operationsList = deduplicatedList;
    settings.updatedAt = Date.now();
    localStorage.setItem(localKey, JSON.stringify(settings));

    // Push to server
    try {
      const docId = `${vesselId}_voyage_settings`;
      const dataToSync = {
        ...settings,
        vesselId,
        updatedAt: Date.now(),
        syncedAt: Date.now()
      };

      const result = await azureCloudClient.push('sr.voyage_settings.v1', docId, dataToSync);

      if (result.success) {
        devLog(`[FT-219-SERVER] Pushed cleaned data for vessel ${vesselId} (${removedCount} duplicates removed)`);
      } else {
        devError(`[FT-219-SERVER] Push failed for vessel ${vesselId}:`, result.error);
      }
    } catch (pushError) {
      devError(`[FT-219-SERVER] Push error for vessel ${vesselId}:`, pushError);
    }
  });
};
